<template>
  <transition :name="animation">
    <div class="dialog modal is-active" v-if="isActive">
      <div class="modal-background" @click="cancel"></div>
      <div class="modal-card animation-content">
        <header class="modal-card-head" v-if="title">
          <p class="modal-card-title">{{ title }}</p>
        </header>

        <section class="modal-card-body" :class="{ 'is-titleless': !title, 'is-flex': hasIcon }">
          <div class="media">
            <div class="media-left" v-if="hasIcon">
              <b-icon :icon="icon ? icon : iconByTpe" :pack="iconPack" :class="type" :both="!icon"
                size="is-large custom-icon">
              </b-icon>
            </div>
            <div class="media-content">
              <p v-html="message"></p>

              <div v-if="hasInput" class="field">
                <div class="control">
                  <input class="input" ref="input" required :value="prompt" :class="{ 'is-danger': validationMessage }"
                    v-bind="tempInputAttrs" @input="(event) => prompt = event.target.value" @keyup.enter="confirm">
                </div>
                <p class="help is-danger">{{ validationMessage }}</p>
              </div>
            </div>
          </div>
        </section>

        <footer class="modal-card-foot">
          <button v-show="canCancel" class="button is-light" ref="cancelButton" @click="cancel">
            {{ cancelText }}
          </button>
          <button class="button" :class="type" ref="confirmButton" @click="confirm">
            {{ confirmText }}
          </button>
        </footer>
      </div>
    </div>
  </transition>
</template>

<script>
import Icon from 'buefy/src/components/icon'
import config from 'buefy/src/utils/config'
import { removeElement } from 'buefy/src/utils/helpers'

export default {
  inheritAttrs: false,
  components: {
    [Icon.name]: Icon
  },
  props: {
    title: String,
    message: String,
    icon: String,
    iconPack: String,
    hasIcon: Boolean,
    type: {
      type: String,
      default: 'is-primary'
    },
    confirmText: {
      type: String,
      default: () => {
        return config.defaultDialogConfirmText
          ? config.defaultDialogConfirmText
          : 'OK'
      }
    },
    cancelText: {
      type: String,
      default: () => {
        return config.defaultDialogCancelText
          ? config.defaultDialogCancelText
          : 'Cancel'
      }
    },
    animation: {
      type: String,
      default: 'zoom-out'
    },
    canCancel: {
      type: Boolean,
      default: true
    },
    hasInput: Boolean, // Used internally to know if it's prompt
    inputPlaceholder: String, // Deprecated
    inputName: String, // Deprecated
    inputMaxlength: [Number, String], // Deprecated
    inputAttrs: Object,
    onConfirm: {
      type: Function,
      default: () => { }
    },
    onCancel: {
      type: Function,
      default: () => { }
    }
  },
  data () {
    // @TODO Remove temporary: inputPlaceholder, inputName and inputMaxlength are deprecated
    const tempInputAttrs = this.inputAttrs || {}
    tempInputAttrs.placeholder = tempInputAttrs.placeholder || this.inputPlaceholder
    tempInputAttrs.name = tempInputAttrs.name || this.inputName
    tempInputAttrs.maxlength = tempInputAttrs.maxlength || this.inputMaxlength

    return {
      isActive: false,
      prompt: tempInputAttrs.value || '',
      validationMessage: '',
      tempInputAttrs
    }
  },
  computed: {
    /**
     * Icon name (MDI) based on the type.
     */
    iconByTpe () {
      switch (this.type) {
        case 'is-info':
          return 'info'
        case 'is-success':
          return 'check_circle'
        case 'is-warning':
          return 'warning'
        case 'is-danger':
          return 'error'
        default:
          return null
      }
    }
  },
  watch: {
    isActive () {
      if (typeof window !== 'undefined') {
        const action = this.isActive ? 'add' : 'remove'
        document.documentElement.classList[action]('is-clipped')
      }
    }
  },
  methods: {
    /**
     * If it's a prompt Dialog, validate the input.
     * Call the onConfirm prop (function) and close the Dialog.
     */
    confirm () {
      if (this.$refs.input !== undefined) {
        if (!this.$refs.input.checkValidity()) {
          this.validationMessage = this.$refs.input.validationMessage
          this.$nextTick(() => this.$refs.input.select())
          return
        }
      }

      this.onConfirm(this.prompt)
      this.close()
    },

    /**
     * Call the onCancel prop (function) and close the Dialog.
     */
    cancel () {
      if (!this.canCancel) return

      this.onCancel()
      this.close()
    },

    /**
     * Close the Dialog.
     */
    close () {
      this.isActive = false

      // Timeout for the animation complete before destroying
      setTimeout(() => {
        this.$destroy()
        removeElement(this.$el)
      }, 150)
    },

    /**
     * Keypress event that is bound to the document.
     */
    keyPress (event) {
      // Esc key
      if (event.keyCode === 27) this.cancel()
    }
  },
  created () {
    if (typeof window !== 'undefined') {
      document.addEventListener('keyup', this.keyPress)
    }
  },
  beforeMount () {
    // Insert the Dialog component in body tag
    document.body.appendChild(this.$el)
  },
  mounted () {
    this.isActive = true

    this.$nextTick(() => {
      if (this.hasInput && this.$refs.input) {
        return this.$refs.input.focus()
      }

      if (this.$refs.confirmButton) {
        this.$refs.confirmButton.focus()
      }
    })
  },
  beforeDestroy () {
    if (typeof window !== 'undefined') {
      document.removeEventListener('keyup', this.keyPress)
    }
  }
}
</script>



// WEBPACK FOOTER //
// Dialog.vue?6b845332